import React from "react";

/**
 * Official AI Bridal Manager™ brand mark.
 *
 * Renders the elegant "AC" serif monogram (extracted from the full
 * lockup as a square crop) on a transparent background.
 *
 * Props:
 *  - size: number (px) — controls width/height square. Default 32.
 *  - className: extra Tailwind classes (e.g. shadow / border).
 *  - title: optional aria-label.
 *
 * Why a separate `logo-mark.png` rather than `logo.png`?
 *   The full lockup includes the "AI Bridal Manager" wordmark below
 *   the monogram (vertical 3:2 aspect). At small sizes (sidebar 32px,
 *   favicon 16-32px) the wordmark becomes illegible noise. The square
 *   monogram crop renders crisp at every size from 16px to 256px,
 *   which is exactly the range we use across the app.
 *
 * Cache busting: the `?v=2` query string is bumped whenever the asset
 * is replaced so deployed clients pick up the new logo immediately
 * instead of serving the previously-cached PNG. Increment on every
 * future logo swap.
 */
export default function BrandLogo({ size = 32, className = "", title = "AI Bridal Manager" }) {
  return (
    <img
      src="/images/logo-mark.png?v=2"
      alt={title}
      width={size}
      height={size}
      style={{ width: size, height: size }}
      // `object-contain` keeps the full monogram visible at any container
      // aspect — switching from the previous `object-cover` because the
      // new asset is already square and pre-padded with transparent
      // breathing room, no edge cropping needed.
      className={`shrink-0 object-contain select-none ${className}`}
      draggable={false}
      data-testid="brand-logo"
    />
  );
}
